// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionPlanSelectionPage.tsx

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle } from "lucide-react";
import type { RootState } from "../../../../../redux/store";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import { setSelectedSubscriptionPlan } from "../../../../../redux/slice/subscription/subscriptionSlice";
import {
    useGetCommercialModelsByLocationQuery,
    type CommercialModel,
} from "../../../../../api/commercialModels/commercialModelsApiSlice";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../../hooks/useSubscriptionUrlSync";
import KeyHandoverNavigateNextAction from "../../common/util/KeyHandoverNavigateNextAction";

/**
 * Plan Selection Content
 * Handles hydration and displays plan selection UI
 */
function PlanSelectionContent() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedLocation = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedLocation
    );

    const {
        data: subscriptionPlans,
        isLoading: plansLoading,
        isError,
    } = useGetCommercialModelsByLocationQuery(selectedLocation?.id || "", {
        skip: !selectedLocation?.id,
    });

    const hydratedRef = React.useRef(false);

    const locationHydrated = useLocationHydration();

    // Hydrate subscription state from URL
    useSubscriptionHydration();
    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);
    // Mark as hydrated after first render
    React.useEffect(() => {
        if (!hydratedRef.current) {
            hydratedRef.current = true;
        }
    }, []);

    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );

    // Currency formatter based on current locale
    const formatCurrency = React.useMemo(() => {
        return new Intl.NumberFormat(i18n.language || "en", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
        });
    }, [i18n.language]);

    // Find monthly and yearly plans from API
    const monthlyPlan = subscriptionPlans?.find((m) => m.recurrence === "monthly");
    const yearlyPlan = subscriptionPlans?.find((m) => m.recurrence === "yearly");

    const handleSelectPlan = (plan: CommercialModel) => {
        dispatch(setSelectedSubscriptionPlan(plan));
    };

    // Check if a plan is selected
    const isSelected = (plan: CommercialModel) => selectedPlan?.id === plan.id;

    if (!locationHydrated || plansLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex flex-col items-center gap-4 py-12 text-center px-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <p className="text-slate-600">
                    {t("book_location.keys.subscription_plan.error_loading", {
                        defaultValue: "Failed to load subscription plans. Please try again.",
                    })}
                </p>
            </div>
        );
    }

    if (!subscriptionPlans || subscriptionPlans.length === 0) {
        return (
            <div className="flex flex-col items-center gap-4 py-12 text-center px-4">
                <AlertCircle className="h-12 w-12 text-amber-500" />
                <div className="space-y-2">
                    <p className="text-lg font-semibold text-slate-900">
                        {t("book_location.keys.subscription_plan.unavailable_title", {
                            defaultValue: "Subscription Unavailable",
                        })}
                    </p>
                    <p className="text-sm text-slate-600 max-w-md">
                        {t("book_location.keys.subscription_plan.unavailable_message", {
                            defaultValue:
                                "We're unable to create a subscription at this location right now. All available lockers are currently in use. Please try again later or contact support.",
                        })}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p
                className="-mt-5 text-xs text-muted-foreground text-center"
                role="status"
                aria-live="polite"
            >
                {t("book_location.keys.step_counter", {
                    current: 1,
                    total: 3,
                    defaultValue: "Schritt {{current}} von {{total}}",
                })}
            </p>
            {/* Plan Cards */}
            <div className="flex flex-col gap-4">
                {/* Monthly Plan */}
                {monthlyPlan && (
                    <button
                        onClick={() => handleSelectPlan(monthlyPlan)}
                        className={`
                        relative p-3 border rounded-lg transition-all
                        hover:border-primary hover:shadow-md text-left
                        ${
                            isSelected(monthlyPlan)
                                ? "border-primary bg-primary/5 shadow-md"
                                : "border-slate-200 bg-white"
                        }
                    `}
                    >
                        {isSelected(monthlyPlan) && (
                            <div className="absolute top-2 right-2">
                                <div className="bg-primary text-white rounded-full p-0.5">
                                    <Check className="h-3 w-3" />
                                </div>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <div className="flex items-baseline gap-2">
                                <span className="text-lg font-semibold text-slate-900">
                                    {t("book_location.keys.subscription_plan.monthly_title", {
                                        defaultValue: "Monthly Plan",
                                    })}
                                </span>
                                <span className="text-xs text-slate-500">
                                    {t("book_location.keys.subscription_plan.billed_monthly", {
                                        defaultValue: "Billed Monthly",
                                    })}
                                </span>
                            </div>

                            <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-bold text-primary">
                                    {formatCurrency.format(monthlyPlan.price)}
                                </span>
                                <span className="text-sm text-slate-600">
                                    {t("book_location.keys.subscription_plan.per_month", {
                                        defaultValue: "/month",
                                    })}
                                </span>
                            </div>

                            <p className="text-xs text-slate-600 mt-1">
                                {t("book_location.keys.subscription_plan.monthly_description", {
                                    defaultValue: "Perfect for short-term needs. Cancel anytime.",
                                })}
                            </p>

                            <ul className="text-xs text-slate-600 space-y-1 mt-2">
                                <li className="flex items-start gap-1.5">
                                    <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                    <span>
                                        {t(
                                            "book_location.keys.subscription_plan.benefit_unlimited",
                                            {
                                                defaultValue: "Unlimited key handovers",
                                            }
                                        )}
                                    </span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                    <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                    <span>
                                        {t("book_location.keys.subscription_plan.benefit_cancel", {
                                            defaultValue: "Cancel monthly",
                                        })}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </button>
                )}

                {/* Yearly Plan */}
                {yearlyPlan && (
                    <button
                        onClick={() => handleSelectPlan(yearlyPlan)}
                        className={`
                        relative p-3 border rounded-lg transition-all
                        hover:border-primary hover:shadow-md text-left
                        ${
                            isSelected(yearlyPlan)
                                ? "border-primary bg-primary/5 shadow-md"
                                : "border-slate-200 bg-white"
                        }
                    `}
                    >
                        {/* Best Value Badge */}
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                                {t("book_location.keys.subscription_plan.best_value", {
                                    defaultValue: "Best Value",
                                })}
                            </span>
                        </div>

                        {isSelected(yearlyPlan) && (
                            <div className="absolute top-2 right-2">
                                <div className="bg-primary text-white rounded-full p-0.5">
                                    <Check className="h-3 w-3" />
                                </div>
                            </div>
                        )}

                        <div className="space-y-1.5 mt-0.5">
                            <div className="flex items-baseline gap-2">
                                <span className="text-lg font-semibold text-slate-900">
                                    {t("book_location.keys.subscription_plan.yearly_title", {
                                        defaultValue: "Yearly Plan",
                                    })}
                                </span>
                                <span className="text-xs text-slate-500">
                                    {t("book_location.keys.subscription_plan.billed_yearly", {
                                        defaultValue: "Billed Annually",
                                    })}
                                </span>
                            </div>

                            <div className="flex items-baseline gap-1.5">
                                <span className="text-2xl font-bold text-primary">
                                    {formatCurrency.format(yearlyPlan.price)}
                                </span>
                                <span className="text-sm text-slate-600">
                                    {t("book_location.keys.subscription_plan.per_year", {
                                        defaultValue: "/year",
                                    })}
                                </span>
                                {monthlyPlan && (
                                    <span className="text-xs text-slate-500 line-through ml-1">
                                        {formatCurrency.format(monthlyPlan.price * 12)}
                                    </span>
                                )}
                            </div>

                            <p className="text-xs text-slate-600 mt-1">
                                {t("book_location.keys.subscription_plan.best_for_landlords_generic", {
                                    defaultValue: "Best for landlords with multiple properties.",
                                })}
                            </p>

                            <ul className="text-xs text-slate-600 space-y-1 mt-2">
                                <li className="flex items-start gap-1.5">
                                    <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                    <span>
                                        {t(
                                            "book_location.keys.subscription_plan.benefit_unlimited",
                                            {
                                                defaultValue: "Unlimited key handovers",
                                            }
                                        )}
                                    </span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                    <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                    <span className="font-medium text-green-600">
                                        {t("book_location.keys.subscription_plan.best_value_long_term", {
                                            defaultValue: "Best value for long-term use",
                                        })}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * Subscription Plan Selection Page
 *
 * Flow 2: New Subscription - Step 1
 * User selects between Monthly and Yearly subscription plans
 */
const SubscriptionPlanSelectionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );

    const handleNext = () => {
        navigate({
            pathname: routes.keyHandoverSubscriptionHostDataPath,
            search: location.search,
        });
    };

    const bottomActions = (
        <KeyHandoverNavigateNextAction
            onNext={handleNext}
            enabled={Boolean(selectedPlan)}
            bounceKey={selectedPlan?.id}
        />
    );

    const { t } = useTranslation();

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_plan.header_text", {
                defaultValue: "Choose Your Plan",
            })}
            backTo={routes.keyHandoverPaymentModelPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <PlanSelectionContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionPlanSelectionPage;
